/**
 * Ownship + track rendering. Driven from the store, drawn as a single GeoJSON
 * source so we can update it in place without re-adding layers.
 *
 * Layers drawn (bottom -> top):
 *   - secondary tracks (faded)
 *   - primary track (solid)
 *   - past-track time marks
 *   - COG/SOG ground vector (two arrowheads)
 *   - water/heading vector (one arrowhead)
 *   - wind vector (met wind barb, strength as feathers) [optional]
 *   - tide vector (set/rate arrow, three arrowheads)  [optional]
 *   - boat marker (oriented triangle)
 *
 * Which of the four vectors draw is driven by the own-ship vector toggles
 * (OwnVectorRender). COG/SOG and heading come from the fix; wind and tide are
 * sampled by the caller and passed in, so this module stays pure and just draws
 * what it's handed. When a toggle is on but no sample is supplied (no source for
 * that vector) nothing is drawn for it, and the menu explains the missing source.
 */
import type { Map as MaplibreMap } from 'maplibre-gl';
import type { FeatureCollection } from 'geojson';
import { rhumbDestination } from '@/nav';
import type { OwnshipState } from '@/store';
import { getPalette, type S52Palette } from './style/palettes';
import { ownshipIconHeading } from './ownshipIconHeading';
import { hullRing, hullWorthDrawing } from '@/ownship/hullOutline';
import type { Palette } from '@/store';
import { timeMarks, trackSegments, type Track } from '@/ownship';
import { windBarbFeatures, barbScaleForZoom } from './windBarb';

const SOURCE_ID = 'ownship';

/**
 * Which own-ship vectors to draw, plus the sampled wind/tide the caller feeds in.
 * COG/SOG (ground) and heading (water track) come straight off the fix, so they
 * only need their on/off flag. Wind and tide need a data source, so the caller
 * samples them and passes the value; a null sample means "toggle on but no source",
 * and we simply draw nothing for it.
 */
export interface OwnVectorRender {
  cogsog: boolean;
  heading: boolean;
  /** Which boat symbol to draw: an icon id registered by registerPlotIcons. */
  boatIcon?: string;
  /**
   * The boat's own measurements and where the GNSS antenna sits on it. Given
   * these, and enough zoom for the hull to be bigger than the symbol, we draw
   * the boat at its real size in its real place rather than as a marker.
   */
  hull?: { lengthM: number; beamM: number; fromBowM: number; fromCentreM: number } | null;
  /** Ground resolution at the boat, metres per screen pixel, for that decision. */
  metresPerPixel?: number;
  /**
   * Whether a heading SOURCE exists at all, separately from whether the user
   * wants the heading VECTOR drawn. The two were the same flag, so turning the
   * vector off would also have left the boat symbol with nothing to point at.
   * Defaults to the vector flag for callers that have not been updated.
   */
  hasHeadingSource?: boolean;
  wind: boolean;
  tide: boolean;
  /** Wind at the boat: the direction the wind blows FROM (deg true) and speed (kn). */
  windSample?: { dirFromDeg: number; speedKn: number } | null;
  /** Tidal stream at the boat: set (deg true the stream flows TOWARD) and rate (kn). */
  tideSample?: { setDeg: number; rateKn: number } | null;
}

/**
 * The historic default: ground + water track on, wind + tide off. Keeps every
 * existing caller (and the layer-order tests) drawing exactly as before when they
 * don't pass a toggle set.
 */
/**
 * What the boat symbol was last pointed at, so a boat that stops does not snap
 * to north or spin on GPS jitter. Module-level because the features are rebuilt
 * from scratch on every fix and there is exactly one own ship.
 */
let lastIconHeading: number | null = null;

export const DEFAULT_VECTORS: OwnVectorRender = { cogsog: true, heading: true, wind: false, tide: false };

/**
 * The ownship layer ids, bottom -> top in draw order. Also the order we re-seat
 * them in so the boat marker always ends up on the very top.
 */
const OWNSHIP_LAYER_IDS = [
  'ownship-accuracy-fill',
  'ownship-accuracy-line',
  'ownship-secondary-track',
  'ownship-primary-track',
  'ownship-time-marks',
  'ownship-cogsog',
  'ownship-cogsog-arrow',
  'ownship-heading',
  'ownship-heading-arrow',
  'ownship-wind',
  'ownship-wind-pennant',
  'ownship-tide',
  'ownship-tide-arrow',
  'ownship-hull-fill',
  'ownship-hull-line',
  'ownship-boat',
] as const;

/**
 * Recolour the ground vector (the COG/SOG line and its double arrowhead) after
 * the fact. Null returns it to the palette accent. A setPaintProperty rather
 * than a layer rebuild, because a colour change should cost a colour change
 * (fb-msmb2bmg-7).
 */
export function applyOwnVectorColour(map: MaplibreMap, colour: string | null, accent: string): void {
  const c = colour ?? accent;
  try {
    if (map.getLayer('ownship-cogsog')) map.setPaintProperty('ownship-cogsog', 'line-color', c);
    if (map.getLayer('ownship-cogsog-arrow')) map.setPaintProperty('ownship-cogsog-arrow', 'text-color', c);
  } catch {
    // A mid-styleswap call can race the layer; the ensure path recolours next pass.
  }
}

/**
 * Lift the ownship layers to the top of the stack, in draw order, so the boat is
 * never buried. This matters because the training/vector chart cells load
 * asynchronously: if a fix (the sim starts fast in training mode) creates the
 * ownship layers before the cells finish loading, addCellToMap appends the cell
 * layers ABOVE ownship. On a raster provider the cells are hidden, so it never
 * showed; on the vector provider their opaque depth/land fills are visible and
 * would cover the boat. Re-seating on top after the cells are in keeps it visible.
 * moveLayer with no `before` moves a layer to the top, so walking bottom -> top
 * leaves the boat marker highest. Missing layers are skipped.
 */
export function raiseOwnshipLayers(map: MaplibreMap): void {
  for (const id of OWNSHIP_LAYER_IDS) {
    if (map.getLayer(id)) map.moveLayer(id);
  }
}

/**
 * A geographic circle (closed ring of [lon, lat]) of the given radius in metres,
 * centred on the boat. Drawn as a real polygon so the accuracy ring sits at true
 * scale on the chart: it grows and shrinks in metres and is only really visible
 * zoomed in, which is honest, a few-metre error genuinely is sub-pixel at an
 * overview zoom. The poor-accuracy alarm is what flags a bad fix when the ring
 * is too small to see.
 */
function metresCircle(center: [number, number], radiusM: number, steps = 64): [number, number][] {
  const radiusNm = radiusM / 1852; // 1 nautical mile = 1852 m
  const ring: [number, number][] = [];
  for (let i = 0; i <= steps; i++) {
    ring.push(rhumbDestination(center, (360 * i) / steps, radiusNm));
  }
  return ring;
}

function rgb(c: [number, number, number]): string {
  return `rgb(${c[0]}, ${c[1]}, ${c[2]})`;
}

/** Green (good) through amber to red (poor), by the error in metres. */
function accuracyColor(accM: number): string {
  const stops: [number, [number, number, number]][] = [
    [3, [46, 204, 113]], // green
    [7, [244, 194, 10]], // amber
    [13, [231, 76, 60]], // red
  ];
  if (accM <= stops[0][0]) return rgb(stops[0][1]);
  const last = stops[stops.length - 1];
  if (accM >= last[0]) return rgb(last[1]);
  for (let i = 0; i < stops.length - 1; i++) {
    const [a, ca] = stops[i];
    const [b, cb] = stops[i + 1];
    if (accM >= a && accM <= b) {
      const t = (accM - a) / (b - a);
      return rgb([
        Math.round(ca[0] + (cb[0] - ca[0]) * t),
        Math.round(ca[1] + (cb[1] - ca[1]) * t),
        Math.round(ca[2] + (cb[2] - ca[2]) * t),
      ]);
    }
  }
  return rgb(last[1]);
}

/**
 * The own-ship feature collection. Exported for tests: the hull was added to
 * the types, the layers and the geometry but never actually emitted, and
 * nothing caught it because an unused optional field typechecks perfectly
 * (fb-mscw4w5r-0). Testing the built features is what closes that gap.
 */
/**
 * The line and time-mark features for one track, cached on the track OBJECT.
 *
 * A WeakMap keyed on the track itself is the whole trick: the store rebuilds the
 * Track object only when a point is genuinely appended, so an unchanged track is
 * the same object and hits the cache, and a changed one is a new object that
 * misses it. Nothing has to be invalidated by hand, and an old track is
 * collected the moment nothing points at it any more.
 */
const trackFeatureCache = new WeakMap<Track, GeoJSON.Feature[]>();

function trackFeatures(t: Track): GeoJSON.Feature[] {
  const hit = trackFeatureCache.get(t);
  if (hit) return hit;
  const out: GeoJSON.Feature[] = [];
  for (const seg of trackSegments(t)) {
    if (seg.length < 2) continue;
    out.push({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: seg.map((p) => p.position) },
      properties: { kind: 'track', trackKind: t.kind },
    });
  }
  // Time-marks run off the WHOLE primary track, not per segment, so the elapsed
  // clock is anchored to the true track start (t0) and keeps counting across a
  // source-change gap instead of restarting after each break. timeMarks skips
  // any mark that would land inside a break span. Only the live primary track —
  // saved secondary tracks would clutter the chart.
  if (t.kind === 'primary') {
    for (const m of timeMarks(t)) {
      out.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: m.position },
        properties: { kind: 'time-mark', label: m.label },
      });
    }
  }
  trackFeatureCache.set(t, out);
  return out;
}

export function buildFeatureCollection(
  ownship: OwnshipState,
  tracks: Record<string, Track>,
  vectorMin: number,
  dimmed: boolean,
  vectors: OwnVectorRender = DEFAULT_VECTORS,
  barbScale = 1,
): FeatureCollection {
  const features: GeoJSON.Feature[] = [];

  // --- Tracks --- drawn segment by segment, breaking at source-change gaps so
  // the breadcrumb never joins across an impossible jump.
  //
  // Cached on the track object's identity, because this whole collection is
  // rebuilt on EVERY fix (own ship has moved, so the boat and its vectors must
  // be redrawn) while the track itself only changes when a point is actually
  // appended, which the minimum-step gate makes a good deal rarer. Without the
  // cache every fix walked every point of the track and copied every coordinate,
  // so the cost of a fix grew with how long the app had been running: fine at
  // breakfast, heavy by midnight, and quadratic over a night at anchor.
  for (const t of Object.values(tracks)) {
    for (const f of trackFeatures(t)) features.push(f);
  }

  if (ownship.position) {
    // --- Accuracy ring: the area of precision around the boat, coloured by how
    // good the fix is. Drawn first so it sits under the vectors and the boat. ---
    if (ownship.accuracyM != null) {
      features.push({
        type: 'Feature',
        geometry: { type: 'Polygon', coordinates: [metresCircle(ownship.position, ownship.accuracyM)] },
        properties: { kind: 'accuracy', color: accuracyColor(ownship.accuracyM), dimmed },
      });
    }

    // --- Water track (heading through the water): length is the chosen vector
    // time run at STW when we have it, so it grows and shrinks with boat speed
    // like the ground vector below; falls back to a fixed length when STW is
    // missing. Marked with a single arrowhead (paper-chart water-track
    // convention). The time-ahead is the user's own-ship vector setting. ---
    if (vectors.heading) {
      const waterNm = ownship.stw > 0 ? ownship.stw * (vectorMin / 60) : 0.5;
      const headTip = rhumbDestination(ownship.position, ownship.heading, waterNm);
      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: [ownship.position, headTip] },
        properties: { kind: 'heading', dimmed },
      });
    }

    // --- COG/SOG ground vector: length proportional to SOG over the chosen
    // vector time (the user's own-ship vector-length setting, minutes). ---
    if (vectors.cogsog && ownship.sog > 0) {
      const runNm = ownship.sog * (vectorMin / 60);
      const cogTip = rhumbDestination(ownship.position, ownship.cog, runNm);
      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: [ownship.position, cogTip] },
        properties: { kind: 'cogsog', dimmed },
      });
    }

    // --- Wind vector: a standard meteorological wind BARB off the boat, so the
    // strength reads at a glance (half feather = 5 kn, full = 10 kn, pennant =
    // 50 kn) rather than a plain arrow. The staff points in the direction the wind
    // comes FROM, the same convention as the weather-overlay barbs, and the shared
    // windBarb builder draws the identical barb so the two never disagree. Only
    // when the toggle is on and the caller handed us a wind sample with real speed. ---
    if (vectors.wind && vectors.windSample && vectors.windSample.speedKn > 0) {
      for (const f of windBarbFeatures(
        ownship.position,
        vectors.windSample.dirFromDeg,
        vectors.windSample.speedKn,
        barbScale,
        { line: 'wind-line', pennant: 'wind-pennant' },
      )) {
        features.push({ ...f, properties: { ...f.properties, dimmed } });
      }
    }

    // --- Tide vector: an arrow in the direction the stream SETS (flows toward),
    // its length the distance the stream would carry us over the vector time, so
    // it scales with rate just like the ground vector scales with SOG. Only when
    // the toggle is on and the caller handed us a stream sample with a real rate. ---
    if (vectors.tide && vectors.tideSample && vectors.tideSample.rateKn > 0) {
      const tideNm = vectors.tideSample.rateKn * (vectorMin / 60);
      const tideTip = rhumbDestination(ownship.position, vectors.tideSample.setDeg, tideNm);
      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: [ownship.position, tideTip] },
        properties: { kind: 'tide', dimmed },
      });
    }

    // --- Boat marker: a pixel-sized point. ---
    // The heading vector line above already shows direction. The point
    // itself is rendered by circle layers in ensureOwnshipLayer below so
    // it stays visible at any zoom (geographic-sized polygons disappear
    // when the boat is small relative to the chart scale).
    // Which way to point the symbol. A real heading if we have one, else COG
    // while moving, else hold the last. See ownshipIconHeading.ts: without this
    // the symbol pointed at true north on any device with no compass, because
    // ownship.heading simply stays at its initial 0 there.
    const hasHeadingSource = vectors.hasHeadingSource ?? vectors.heading;
    const boatIcon = vectors.boatIcon ?? 'plot-boat-ardent';
    const iconHeading = ownshipIconHeading({
      heading: hasHeadingSource ? ownship.heading : null,
      cog: ownship.cog,
      sog: ownship.sog,
      last: lastIconHeading,
    });
    if (iconHeading != null) lastIconHeading = iconHeading;

    // Zoomed in far enough, and knowing the boat, draw the hull at its real size
    // and in its real place: the fix is the ANTENNA, and the bow is what touches
    // things. Otherwise the symbol, which is clearer when the boat is only a few
    // pixels long and still says which way we point.
    const ring =
      vectors.hull && vectors.metresPerPixel != null && iconHeading != null &&
      hullWorthDrawing(vectors.hull.lengthM, vectors.metresPerPixel)
        ? hullRing({
            position: ownship.position,
            headingDeg: iconHeading ?? 0,
            lengthM: vectors.hull.lengthM,
            beamM: vectors.hull.beamM,
            fromBowM: vectors.hull.fromBowM,
            fromCentreM: vectors.hull.fromCentreM,
          })
        : null;
    if (ring) {
      features.push({
        type: 'Feature',
        geometry: { type: 'Polygon', coordinates: [ring] },
        properties: { kind: 'hull', dimmed },
      });
      // The hull IS the boat at this scale, so no symbol on top of it.
      return { type: 'FeatureCollection', features };
    }

    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: ownship.position },
      // Null would leave icon-rotate with nothing; 0 is the honest fallback for
      // a boat that has never moved and has no compass, and the symbol is a
      // point rather than a shape at that size anyway.
      // The icon travels on the feature rather than being baked into the layer,
      // so switching the symbol repaints without rebuilding the style.
      properties: { kind: 'boat', heading: iconHeading ?? 0, icon: boatIcon, dimmed },
    });
  }

  return { type: 'FeatureCollection', features };
}

/** Mount the ownship source and its layers on a freshly-styled map. */
export function ensureOwnshipLayer(
  map: MaplibreMap,
  ownship: OwnshipState,
  tracks: Record<string, Track>,
  palette: Palette,
  vectorMin = 6,
  dimmed = false,
  vectors: OwnVectorRender = DEFAULT_VECTORS,
): void {
  const p: S52Palette = getPalette(palette);
  if (!map.getSource(SOURCE_ID)) {
    map.addSource(SOURCE_ID, {
      type: 'geojson',
      data: buildFeatureCollection(ownship, tracks, vectorMin, dimmed, vectors, barbScaleForZoom(map.getZoom())),
    });
  }

  // Accuracy ring sits at the bottom of the ownship stack, under the vectors and
  // the boat marker, so it never hides the boat. A real metres-scale polygon, so
  // it's true to the chart (and tiny until you zoom in).
  if (!map.getLayer('ownship-accuracy-fill')) {
    map.addLayer({
      id: 'ownship-accuracy-fill',
      type: 'fill',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'accuracy'],
      paint: {
        'fill-color': ['get', 'color'],
        // Dim the ring when the fix is stale so it doesn't imply a live precision.
        'fill-opacity': ['case', ['boolean', ['get', 'dimmed'], false], 0.07, 0.16],
      },
    });
  }
  if (!map.getLayer('ownship-accuracy-line')) {
    map.addLayer({
      id: 'ownship-accuracy-line',
      type: 'line',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'accuracy'],
      paint: {
        'line-color': ['get', 'color'],
        'line-width': 1.3,
        'line-opacity': ['case', ['boolean', ['get', 'dimmed'], false], 0.4, 0.9],
      },
    });
  }
  if (!map.getLayer('ownship-secondary-track')) {
    map.addLayer({
      id: 'ownship-secondary-track',
      type: 'line',
      source: SOURCE_ID,
      filter: ['all', ['==', ['get', 'kind'], 'track'], ['==', ['get', 'trackKind'], 'secondary']],
      paint: {
        'line-color': p.CHGRD,
        'line-width': 1.4,
        'line-dasharray': [3, 2],
        'line-opacity': 0.7,
      },
    });
  }
  if (!map.getLayer('ownship-primary-track')) {
    map.addLayer({
      id: 'ownship-primary-track',
      type: 'line',
      source: SOURCE_ID,
      filter: ['all', ['==', ['get', 'kind'], 'track'], ['==', ['get', 'trackKind'], 'primary']],
      paint: {
        'line-color': p.UI_ACCENT,
        'line-width': 1.8,
      },
    });
  }
  if (!map.getLayer('ownship-time-marks')) {
    map.addLayer({
      id: 'ownship-time-marks',
      type: 'symbol',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'time-mark'],
      layout: {
        'text-field': ['get', 'label'],
        'text-font': ['Open Sans Regular'],
        'text-size': 10,
        'text-offset': [0.6, 0],
        'text-anchor': 'left',
      },
      paint: {
        'text-color': p.UI_ACCENT,
        'text-halo-color': p.DEPDW,
        'text-halo-width': 1.2,
      },
    });
  }
  if (!map.getLayer('ownship-cogsog')) {
    map.addLayer({
      id: 'ownship-cogsog',
      type: 'line',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'cogsog'],
      paint: {
        // Ground track is the primary line: solid.
        'line-color': p.UI_ACCENT,
        'line-width': 2.2,
        'line-opacity': ['case', ['boolean', ['get', 'dimmed'], false], 0.35, 1],
      },
    });
  }
  // Ground track: two arrowheads, the paper-chart course-over-ground symbol.
  if (!map.getLayer('ownship-cogsog-arrow')) {
    map.addLayer({
      id: 'ownship-cogsog-arrow',
      type: 'symbol',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'cogsog'],
      layout: {
        'symbol-placement': 'line-center',
        'text-field': '>>',
        'text-font': ['Open Sans Regular'],
        'text-size': 16,
        'text-rotation-alignment': 'map',
        'text-keep-upright': false,
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: { 'text-color': p.UI_ACCENT, 'text-halo-color': p.UI_BG, 'text-halo-width': 1.4, 'text-opacity': ['case', ['boolean', ['get', 'dimmed'], false], 0.35, 1] },
    });
  }
  if (!map.getLayer('ownship-heading')) {
    map.addLayer({
      id: 'ownship-heading',
      type: 'line',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'heading'],
      paint: {
        // Water track is the secondary line: dashed.
        'line-color': p.CHBLK,
        'line-width': 1.4,
        'line-dasharray': [2, 1.5],
        'line-opacity': ['case', ['boolean', ['get', 'dimmed'], false], 0.35, 1],
      },
    });
  }
  // Water track: one arrowhead, the paper-chart course-through-water symbol.
  if (!map.getLayer('ownship-heading-arrow')) {
    map.addLayer({
      id: 'ownship-heading-arrow',
      type: 'symbol',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'heading'],
      layout: {
        'symbol-placement': 'line-center',
        'text-field': '>',
        'text-font': ['Open Sans Regular'],
        'text-size': 16,
        'text-rotation-alignment': 'map',
        'text-keep-upright': false,
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: { 'text-color': p.CHBLK, 'text-halo-color': p.UI_BG, 'text-halo-width': 1.4, 'text-opacity': ['case', ['boolean', ['get', 'dimmed'], false], 0.35, 1] },
    });
  }
  // Wind vector: a standard met wind barb, drawn in a distinct wind colour so it
  // never reads as a course line. The staff and feathers are the line part; the
  // pennants (50 kn triangles) are a fill on top. Solid, the way a barb is drawn.
  if (!map.getLayer('ownship-wind')) {
    map.addLayer({
      id: 'ownship-wind',
      type: 'line',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'wind-line'],
      paint: {
        'line-color': '#12a5c4',
        'line-width': 1.6,
        'line-opacity': ['case', ['boolean', ['get', 'dimmed'], false], 0.35, 1],
      },
    });
  }
  if (!map.getLayer('ownship-wind-pennant')) {
    map.addLayer({
      id: 'ownship-wind-pennant',
      type: 'fill',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'wind-pennant'],
      paint: {
        'fill-color': '#12a5c4',
        'fill-opacity': ['case', ['boolean', ['get', 'dimmed'], false], 0.35, 0.9],
      },
    });
  }
  // Tide vector: an arrow in the set direction, in a distinct tide colour. Length
  // scales with rate over the vector time, the same basis as the ground vector.
  if (!map.getLayer('ownship-tide')) {
    map.addLayer({
      id: 'ownship-tide',
      type: 'line',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'tide'],
      paint: {
        'line-color': '#1f9d6b',
        'line-width': 1.8,
        'line-opacity': ['case', ['boolean', ['get', 'dimmed'], false], 0.35, 1],
      },
    });
  }
  if (!map.getLayer('ownship-tide-arrow')) {
    map.addLayer({
      id: 'ownship-tide-arrow',
      type: 'symbol',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'tide'],
      layout: {
        'symbol-placement': 'line-center',
        // Three arrowheads: the RYA tidal-stream convention (one = water track,
        // two = ground track, three = tidal stream), so the stream is never
        // mistaken for a course line.
        'text-field': '>>>',
        'text-font': ['Open Sans Regular'],
        'text-size': 15,
        'text-rotation-alignment': 'map',
        'text-keep-upright': false,
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: { 'text-color': '#1f9d6b', 'text-halo-color': p.UI_BG, 'text-halo-width': 1.4, 'text-opacity': ['case', ['boolean', ['get', 'dimmed'], false], 0.35, 1] },
    });
  }
  // The hull at real size, drawn only when it is bigger than the symbol would
  // be. Filled faintly so the chart underneath still reads through it: this
  // shows where the boat IS, it is not there to hide what it sits on.
  if (!map.getLayer('ownship-hull-fill')) {
    map.addLayer({
      id: 'ownship-hull-fill',
      type: 'fill',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'hull'],
      paint: {
        // The arrow it replaces is solid, so the hull is too: zooming in should
        // read as the same boat getting bigger, not a fainter thing taking its
        // place (fb-mscxlrg4-0).
        'fill-color': p.UI_ACCENT ?? '#c62828',
        'fill-opacity': ['case', ['boolean', ['get', 'dimmed'], false], 0.4, 1],
      },
    });
  }
  if (!map.getLayer('ownship-hull-line')) {
    map.addLayer({
      id: 'ownship-hull-line',
      type: 'line',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'hull'],
      paint: {
        // The same white keyline the arrow carries, so it holds its shape over
        // dark chart fills and satellite alike.
        'line-color': 'rgba(255,255,255,0.95)',
        'line-width': 1.8,
        'line-opacity': ['case', ['boolean', ['get', 'dimmed'], false], 0.4, 1],
      },
    });
  }
  // Boat marker — Ardent arrow icon rotated by heading.
  if (!map.getLayer('ownship-boat')) {
    map.addLayer({
      id: 'ownship-boat',
      type: 'symbol',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'boat'],
      layout: {
        // Red whenever the position is not a live fix of ours. A per-feature
        // icon override still wins when the fix IS live; when it is not, the
        // red mark wins over everything, because that is the thing worth
        // seeing.
        'icon-image': [
          'case',
          ['boolean', ['get', 'dimmed'], false],
          'plot-boat-ardent-unfixed',
          ['coalesce', ['get', 'icon'], 'plot-boat-ardent'],
        ],
        'icon-size': 1,
        'icon-rotate': ['get', 'heading'],
        'icon-rotation-alignment': 'map',
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
      // Full opacity either way. The unfixed boat used to be the ordinary one
      // faded to 40%, and a faded boat on a busy chart is easy to look straight
      // past, which is the opposite of what a position you should not trust
      // needs to do. It is red now (see icons.ts) and it is not hiding.
      paint: {
        'icon-opacity': 1,
      },
    });
  }

  // Whether we just added the layers or they already existed (created early by an
  // incoming fix, before the async chart cells finished loading), make sure they
  // sit above the cells so the boat is never hidden behind the vector chart fills.
  raiseOwnshipLayers(map);
}

export function updateOwnshipLayer(
  map: MaplibreMap,
  ownship: OwnshipState,
  tracks: Record<string, Track>,
  vectorMin = 6,
  dimmed = false,
  vectors: OwnVectorRender = DEFAULT_VECTORS,
): void {
  const src = map.getSource(SOURCE_ID);
  if (!src) return;
  (src as maplibregl.GeoJSONSource).setData(buildFeatureCollection(ownship, tracks, vectorMin, dimmed, vectors, barbScaleForZoom(map.getZoom())));
}
